import { ReactNode, useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { Button, Input } from "../../../shared-ui/primitives";
import "./RowShell.css";

export interface RowShellProps {
  rowId: string;
  index: number;
  badge?: ReactNode;
  children: ReactNode;
  expanded: boolean;
  onToggleExpanded: () => void;
  workOrderReference: string | null;
  onWorkOrderReferenceChange: (value: string) => void;
  onDelete: () => void;
  readOnly?: boolean;
  rowRef?: React.Ref<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /**
   * Pre-resolved Enter-to-next-field ref/handler for the one remaining
   * expand-only field below — RowShell doesn't need to know its own field
   * index, only NormedWorkRow does (task:
   * docs/NORMAX_PERFORMANCE_DAILY_ENTRY_WORKLINE_NOTE_FORENSIC_REPORT.md,
   * Part B — Napomena moved out to the always-visible core row; only Broj
   * radnog naloga remains here). `undefined` when read-only or not expanded.
   */
  workOrderReferenceFieldProps?: {
    ref: (el: HTMLInputElement | null) => void;
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  };
}

export function RowShell({
  rowId,
  index,
  badge,
  children,
  expanded,
  onToggleExpanded,
  workOrderReference,
  onWorkOrderReferenceChange,
  onDelete,
  readOnly,
  rowRef,
  onKeyDown,
  workOrderReferenceFieldProps,
}: RowShellProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className="nx-row-shell" data-row-id={rowId} ref={rowRef} onKeyDown={onKeyDown}>
      <div className="nx-row-shell__main">
        <span className="nx-row-shell__index tabular-nums">{index}</span>
        {badge}
        <div className="nx-row-shell__fields">{children}</div>
        {!readOnly ? (
          <div className="nx-row-shell__actions">
            <button
              type="button"
              className="nx-row-shell__expand"
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              aria-label="Prošireno — dodatna polja"
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {confirmingDelete ? (
              <span className="nx-row-shell__confirm">
                Obriši ovaj red?
                <Button size="sm" variant="danger" onClick={onDelete}>
                  Da
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  Otkaži
                </Button>
              </span>
            ) : (
              <button
                type="button"
                className="nx-row-shell__delete"
                onClick={() => setConfirmingDelete(true)}
                aria-label="Obriši red"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ) : null}
      </div>
      {expanded ? (
        <div className="nx-row-shell__expanded">
          <Input
            label="Broj radnog naloga"
            value={workOrderReference ?? ""}
            disabled={readOnly}
            onChange={(e) => onWorkOrderReferenceChange(e.target.value)}
            ref={workOrderReferenceFieldProps?.ref}
            onKeyDown={workOrderReferenceFieldProps?.onKeyDown}
          />
        </div>
      ) : null}
    </div>
  );
}
